import { useState, useEffect } from "react";
import Api from "../../Auth/Api";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { getColorsForLabels } from '../../utils/chartColors';
import { customStyles } from '../../utils/selectStyles';
import { RelatorioSkeleton } from '../../Components/Skeletons/RelatorioSkeleton';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function RelatorioAvaliacaoReacao() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewType, setViewType] = useState('pizza'); // pizza, table, bar
    
    // Estados para filtros
    const [unidades, setUnidades] = useState([]);
    const [cursos, setCursos] = useState([]);
    const [filtros, setFiltros] = useState({
        unidade: '',
        curso: '',
        data_inicio: '',
        data_fim: ''
    });

    // Buscar unidades e cursos disponíveis
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [unidadesResponse, cursosResponse] = await Promise.all([
                    Api.get('api/unidades-avaliacao-reacao', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    Api.get('api/cursos-avaliacao-reacao', {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);
                
                setUnidades(unidadesResponse.data);
                setCursos(cursosResponse.data);
            } catch (error) {
                console.error("Erro ao carregar opções de filtro:", error);
            }
        };
        fetchOptions();
    }, [token]);

    // Buscar dados com filtros
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const queryParams = new URLSearchParams();
                
                if (filtros.unidade) queryParams.append('unidade', filtros.unidade);
                if (filtros.curso) queryParams.append('curso', filtros.curso);
                if (filtros.data_inicio) queryParams.append('data_inicio', filtros.data_inicio);
                if (filtros.data_fim) queryParams.append('data_fim', filtros.data_fim);
                
                const response = await Api.get(`api/estatisticas-avaliacao-reacao?${queryParams.toString()}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setData(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Erro ao carregar estatísticas de avaliação de reação:", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [token, filtros]);

    const handleFiltroChange = (campo, valor) => {
        setFiltros(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const limparFiltros = () => {
        setFiltros({
            unidade: '',
            curso: '',
            data_inicio: '',
            data_fim: ''
        });
    };

    const createChartData = (question, responses) => {
        const labels = Object.keys(responses);
        const values = Object.values(responses);
        const backgroundColor = getColorsForLabels(labels);

        return {
            labels,
            datasets: [{
                data: values,
                backgroundColor,
                borderWidth: 1
            }]
        };
    };

    const createBarChartData = (question, responses) => {
        const labels = Object.keys(responses);
        const values = Object.values(responses);
        const backgroundColor = getColorsForLabels(labels);

        return {
            labels,
            datasets: [{
                label: 'Respostas',
                data: values,
                backgroundColor,
                borderWidth: 1
            }]
        };
    };

    const renderVisualization = (question, responses, index) => {
        switch (viewType) {
            case 'pizza':
                return (
                    <div style={{ height: '300px' }}>
                        <Pie
                            data={createChartData(question, responses)}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom' }
                                }
                            }}
                        />
                    </div>
                );
            case 'table':
                return (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Resposta</th>
                                    <th>Quantidade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(responses).map(([response, count], i) => (
                                    <tr key={i}>
                                        <td>{response}</td>
                                        <td>{count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            case 'bar':
                return (
                    <div style={{ height: '300px' }}>
                        <Bar
                            data={createBarChartData(question, responses)}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            stepSize: 1
                                        }
                                    }
                                }
                            }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const handleExportExcel = () => {
        if (!data) return;

        const dataForExcel = Object.entries(data).map(([question, responses]) => {
            const row = {
                'Pergunta': question,
            };
            
            Object.entries(responses).forEach(([response, count]) => {
                row[response] = count;
            });
            
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "AvaliacaoReacao");

        XLSX.writeFile(workbook, "relatorio_avaliacao_reacao.xlsx");
    };

    if (loading) {
        return <RelatorioSkeleton cardCount={4} />;
    }

    return (
        <div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
            <div className="rounded-3 mb-4">
                <div className="mb-3 d-flex justify-content-between align-items-start">
                    <div>
                        <h1 className="text-primary fs-2 fw-bold mb-2">Estatísticas da Avaliação de Reação</h1>
                        <p className="text-muted mb-0">
                            Visualize as estatísticas das avaliações de reação dos usuários.
                        </p>
                    </div>
                <div className="d-flex gap-2">
                    <div className="btn-group">
                        <button 
                            className={`btn ${viewType === 'pizza' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewType('pizza')}
                        >
                            <i className="bi bi-pie-chart"></i> Pizza
                        </button>
                        <button 
                            className={`btn ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewType('table')}
                        >
                            <i className="bi bi-table"></i> Tabela
                        </button>
                        <button 
                            className={`btn ${viewType === 'bar' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewType('bar')}
                        >
                            <i className="bi bi-bar-chart"></i> Colunas
                        </button>
                    </div>
                    <button 
                        className="btn btn-success"
                        onClick={handleExportExcel}
                        disabled={!data}
                    >
                        <i className="bi bi-file-earmark-spreadsheet"></i> Download
                    </button>
                </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <label className="form-label">Unidade</label>
                            <Select
                                options={unidades.map((unidade) => ({
                                    value: unidade,
                                    label: unidade
                                }))}
                                value={filtros.unidade ? { value: filtros.unidade, label: filtros.unidade } : null}
                                onChange={(option) => handleFiltroChange('unidade', option ? option.value : '')}
                                styles={customStyles}
                                isClearable={true}
                                placeholder="Todas as unidades"
                                noOptionsMessage={() => "Nenhuma unidade encontrada."}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label">Curso</label>
                            <Select
                                options={cursos.map((curso) => ({
                                    value: curso.id,
                                    label: curso.nome
                                }))}
                                value={filtros.curso ? cursos.find(c => c.id === filtros.curso) && { value: filtros.curso, label: cursos.find(c => c.id === filtros.curso).nome } : null}
                                onChange={(option) => handleFiltroChange('curso', option ? option.value : '')}
                                styles={customStyles}
                                isClearable={true}
                                placeholder="Todos os cursos"
                                noOptionsMessage={() => "Nenhum curso encontrado."}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Data Início</label>
                            <input 
                                type="date"
                                className="form-control"
                                value={filtros.data_inicio}
                                onChange={(e) => handleFiltroChange('data_inicio', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Data Fim</label>
                            <input 
                                type="date"
                                className="form-control"
                                value={filtros.data_fim}
                                onChange={(e) => handleFiltroChange('data_fim', e.target.value)}
                            />
                        </div>
                        <div className="col-md-1 d-flex align-items-end">
                            <button 
                                className="btn btn-outline-secondary w-100 h-50px"
                                onClick={limparFiltros}
                            >
                                <i className="bi bi-arrow-clockwise"></i> Limpar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {data && Object.entries(data).length > 0 ? (
                    Object.entries(data).map(([question, responses], index) => (
                        <div key={index} className="col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h5 className="card-title mb-4">{question}</h5>
                                    {renderVisualization(question, responses, index)}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-12">
                        <div className="text-center text-muted">
                            <i className="bi bi-info-circle fs-1"></i>
                            <p className="mt-2">Nenhum dado encontrado para os filtros selecionados.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default RelatorioAvaliacaoReacao; 